// src/pages/Jobs/JobCityPage.tsx - Updated with category support and requery functionality
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLoaderData } from 'react-router-dom';
import { Container, Row, Col, Alert } from 'reactstrap';
import Header from '../Home/Header';
import Footer from '../Home/Footer';
import JobListingLayout from './JobListingLayout';
import SEOTags from '../Listing/SEOTags';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import { Job } from '../../models/Job.interface';
import { jobAPI } from '../../services/jobService';

// Interface for useParams compatibility
interface JobCityPageParams extends Record<string, string | undefined> {
  city: string;
  state: string;
  categorySlug?: string; // NEW: Add category support
}

interface JobCityPageLoaderData {
  city: string;
  state: string;
  category?: string; // NEW: Add category support
}

const JobCityPage: React.FC = () => {
  const { city, state, categorySlug } = useParams<JobCityPageParams>();
  const [searchParams, setSearchParams] = useSearchParams();
  const loaderData = useLoaderData() as JobCityPageLoaderData;

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [center, setCenter] = useState<{ latitude: number; longitude: number } | undefined>();
  const [isRequerying, setIsRequerying] = useState(false);

  // Format display names - preserving original logic
  const cityDisplay = city?.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ') || '';

  const stateDisplay = state?.toUpperCase() || '';

  // NEW: Format category display name
  const categoryDisplay = categorySlug?.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ') || '';

  // UPDATED: Enhanced handleJobClick with proper routing to jobid
  const handleJobClick = (job: Job) => {
    const seoUrl = job.jobTitle?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'job';
    const jobPath = `/job/${seoUrl}/${job.id}`;
    console.log('🔍 Generated job path:', jobPath, 'for job:', job);
    window.location.href = jobPath;
  };

  const handleBoundsChange = (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => {
    // Handle map bounds change if needed
    console.log('Bounds changed:', bounds);
  };

  // NEW: Handle requery listings for current map area
  const handleRequeryListings = async (
    newCenter: { latitude: number; longitude: number }, 
    bounds?: { north: number; south: number; east: number; west: number }
  ) => {
    console.log('🔄 Requerying listings for new center:', newCenter);
    console.log('🗺️ Using map bounds:', bounds);
    
    setLoading(true);
    setError(null);
    setIsRequerying(true); // Mark that we're requerying

    try {
      const page = parseInt(searchParams.get('page') || '1');
      const limit = 100;

      const searchParams_obj: any = {
        page,
        limit,
        status: ['Published', 'Hiring', 'Pending Inspection', 'Complete', 'Job In Progress']
      };

      // Add bounds if provided (this will make the API search within the map bounds)
      if (bounds) {
        searchParams_obj.bounds = bounds;
        console.log('📡 Using map bounds for targeted search:', bounds);
      }

      // Add category filter if present
      if (categoryDisplay) {
        searchParams_obj.categories = [categoryDisplay];
      }

      console.log('📡 Making requery API call with params:', searchParams_obj);

      const response = await jobAPI.getJobsByCity(
        cityDisplay,
        stateDisplay,
        searchParams_obj
      );

      console.log('✅ Requery response:', response);

      if (!response || !response.jobs || !Array.isArray(response.jobs)) {
        throw new Error('Invalid response structure from server');
      }

      setJobs(response.jobs);
      setTotalCount(response.total || 0);
      
      // CRITICAL FIX: DON'T update center during requery to prevent zoom out
      // The map should maintain its current position
      console.log('🗺️ NOT updating center to prevent zoom out. Current center maintained:', newCenter);

      console.log('✅ Requery completed successfully:', {
        jobCount: response.jobs.length,
        maintainedCenter: newCenter,
        totalCount: response.total,
        usedBounds: !!bounds
      });

    } catch (err) {
      console.error('❌ Error requerying jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to requery jobs. Please try again.');
    } finally {
      setLoading(false);
      setIsRequerying(false);
    }
  };

  // Fetch jobs for the city using enhanced backend - UPDATED with category support
  useEffect(() => {
    const fetchJobs = async () => {
      if (!city || !state) return;

      setLoading(true);
      setError(null);

      try {
        const page = parseInt(searchParams.get('page') || '1');
        const limit = 100; // Show more jobs for map view

        console.log('🔍 Making API call with params:', {
          cityDisplay,
          stateDisplay,
          categoryDisplay, // NEW: Log category
          page,
          limit
        });

        // UPDATED: Include category in the API call if present
        const searchParams_obj: any = {
          page,
          limit,
          status: ['Published', 'Hiring', 'Pending Inspection', 'Complete', 'Job In Progress'] // Only show active jobs
        };

        // NEW: Add category filter if present
        if (categoryDisplay) {
          searchParams_obj.categories = [categoryDisplay];
        }

        // Use original API call structure with category support
        const response = await jobAPI.getJobsByCity(
          cityDisplay,
          stateDisplay,
          searchParams_obj
        );

        console.log('✅ Frontend received response:', response);

        // Check if response exists and has the expected structure
        if (!response) {
          throw new Error('No response received from server');
        }

        if (!response.jobs || !Array.isArray(response.jobs)) {
          console.error('❌ Invalid response structure:', response);
          throw new Error('Invalid response structure from server');
        }

        // Set the jobs data
        setJobs(response.jobs);
        setTotalCount(response.total || 0);
        setCurrentPage(page);

        console.log('✅ Jobs set successfully:', {
          jobCount: response.jobs.length,
          totalCount: response.total,
          categoryFilter: categoryDisplay || 'none',
          firstJob: response.jobs[0]?.jobTitle
        });

        // Use center from backend response if available
        if (response.center) {
          setCenter({
            latitude: response.center.latitude,
            longitude: response.center.longitude
          });
          console.log('✅ Center set from response:', response.center);
        } else if (response.jobs && response.jobs.length > 0) {
          // Fallback to first job location
          const firstJob = response.jobs[0];
          if (firstJob.propertyAddress?.latitude && firstJob.propertyAddress?.longitude) {
            setCenter({
              latitude: firstJob.propertyAddress.latitude,
              longitude: firstJob.propertyAddress.longitude
            });
            console.log('✅ Center set from first job:', {
              latitude: firstJob.propertyAddress.latitude,
              longitude: firstJob.propertyAddress.longitude
            });
          }
        } else {
          // Last resort: geocode the city if no center provided
          console.log('⚠️ No center found, geocoding city');
          geocodeCity(cityDisplay, stateDisplay);
        }

      } catch (err) {
        console.error('❌ Error fetching jobs:', err);
        setError(err instanceof Error ? err.message : 'Failed to load jobs. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [city, state, categorySlug, cityDisplay, stateDisplay, categoryDisplay, searchParams]);

  // Geocode city to get center coordinates (fallback method) - preserving original
  const geocodeCity = async (cityName: string, stateName: string) => {
    try {
      console.log('🌍 Geocoding city:', cityName, stateName);
      
      // Try backend geocoding first
      try {
        const geocoded = await jobAPI.geocodeLocation({
          query: `${cityName}, ${stateName}`,
          type: 'city'
        });

        console.log('✅ Backend geocoding successful:', geocoded);

        setCenter({
          latitude: geocoded.latitude,
          longitude: geocoded.longitude
        });
        return;
      } catch (backendError) {
        console.warn('⚠️ Backend geocoding failed, falling back to Google Maps:', backendError);
      }

      // Fallback to Google Maps if backend geocoding fails
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        
        geocoder.geocode(
          { address: `${cityName}, ${stateName}` },
          (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              setCenter({
                latitude: location.lat(),
                longitude: location.lng()
              });
              console.log('✅ Google Maps geocoding successful');
            } else {
              console.error('❌ Google Maps geocoding failed:', status);
              // Use a default center if all geocoding fails
              setCenter({ latitude: 39.8283, longitude: -98.5795 }); // Center of US
            }
          }
        );
      } else {
        console.warn('⚠️ Google Maps not available, using default center');
        // Use a default center if Google Maps is not available
        setCenter({ latitude: 39.8283, longitude: -98.5795 }); // Center of US
      }
    } catch (error) {
      console.error('❌ Error in geocoding:', error);
      // Use a default center if all geocoding fails
      setCenter({ latitude: 39.8283, longitude: -98.5795 }); // Center of US
    }
  };

  // UPDATED: Enhanced title and description for category filtering
  const pageTitle = categoryDisplay 
    ? `${categoryDisplay} Jobs in ${cityDisplay}, ${stateDisplay} | Local ${categoryDisplay} Opportunities`
    : `Jobs in ${cityDisplay}, ${stateDisplay} | Local Project Opportunities`;
    
  const pageDescription = categoryDisplay
    ? `Find ${categoryDisplay} job opportunities in ${cityDisplay}, ${stateDisplay}. Browse available ${categoryDisplay} projects from local homeowners and businesses.`
    : `Find construction and home improvement job opportunities in ${cityDisplay}, ${stateDisplay}. Browse available projects from local homeowners and businesses.`;

  // SEO data for breadcrumb - fixed structure to avoid duplication and invalid routes
  const customBreadcrumbs = [
    { title: 'Home', link: '/' },
    { title: 'Jobs', link: '/jobs' },
    { title: stateDisplay, link: `/jobs/${state}` },
    { title: cityDisplay, link: `/jobs/${state}/${city}` }
  ];

  // Only add category if it exists and we're on a category-specific page
  if (categoryDisplay && categorySlug) {
    // Insert category before city, not after
    customBreadcrumbs.splice(3, 0, { title: categoryDisplay, link: `/jobs/category/${categorySlug}` });
  }

  if (error) {
    return (
      <>
        <Header isFullWidth={true} />
        <div className="page-content">
          <AutoBreadCrumb />
          <Container>
            <Row>
              <Col>
                <Alert color="danger" className="my-4">
                  <h4>Error Loading Jobs</h4>
                  <p>{error}</p>
                </Alert>
              </Col>
            </Row>
          </Container>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOTags
        title={pageTitle}
        description={pageDescription}
        keywords={[
          `${cityDisplay} jobs`,
          `${stateDisplay} jobs`,
          ...(categoryDisplay ? [`${categoryDisplay} jobs`, `${categoryDisplay} ${cityDisplay}`] : []),
          'construction jobs',
          'contractor opportunities',
          'home improvement jobs'
        ]}
        canonicalUrl={categoryDisplay 
          ? `/jobs/${state}/${city}/${categorySlug}`
          : `/jobs/${state}/${city}`
        }
      />
      
      <Header isFullWidth={true} />
      
      <div className="page-content">
        <AutoBreadCrumb />
        
        <JobListingLayout
          jobs={jobs}
          loading={loading}
          center={center}
          onJobClick={handleJobClick}
          onBoundsChange={handleBoundsChange}
          onRequeryListings={handleRequeryListings}
          isRequerying={isRequerying}
          title={categoryDisplay 
            ? `${categoryDisplay} Construction Jobs in ${cityDisplay}, ${stateDisplay}`
            : `Jobs in ${cityDisplay}, ${stateDisplay}`
          }
          subtitle={categoryDisplay
            ? `Browse ${categoryDisplay} construction projects in ${cityDisplay}`
            : `Browse construction projects in ${cityDisplay}`
          }
          totalCount={totalCount}
          showMap={true}
        />
      </div>
      
      <Footer />
    </>
  );
};

export default JobCityPage;